import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  Text,
  TextInput,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { X } from 'tabler-icons-react';
import axios from '../../api/axios';
import useAuth from '../../hooks/useAuth';
import { PersonalInfoSchema } from '../../schemas/personal-info.schema';
import { SignUpRequest } from '../../type/auth.types';
import './register-form.styles.css';

import { useState } from 'react';

import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { ErrorResponse } from '../../type/error-response.types';
import { RegisterFormValue } from './register-form.component';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface PersonalInfoFormProps {
  firstForm: UseFormReturnType<RegisterFormValue>;
  toggle: (value?: React.SetStateAction<string> | undefined) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}
function PersonalInfoForm(props: PersonalInfoFormProps) {
  const navigate = useNavigate();
  const { firstForm, toggle, loading, setLoading } = props;

  const [masked, setMasked] = useState(false);

  const { auth, setAuth } = useAuth();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const REGISTER_URL = '/web/auth/signup';
  const [isMNameDisabled, setIsMNameDisabled] = useState<boolean>(false);

  const form = useForm({
    initialValues: firstForm.values,
    schema: joiResolver(PersonalInfoSchema),
  });

  const handleRegisterSubmit = async () => {
    setLoading(true);

    if (!executeRecaptcha) {
      return;
    }

    const recaptchaToken = await executeRecaptcha('register');

    const {
      email,
      username,
      password,
      phoneNumber,

      firstName,
      middleName,
      lastName,
      pin,
    } = form.values;

    const signUpRequest: SignUpRequest = {
      contact: {
        email,
        phone: phoneNumber,
      },
      user: {
        firstName,
        middleName: '.',
        lastName,
        username,
        password,
        pin,
      },
      recaptchaToken,
    };

    if (middleName.trim().length > 0) {
      signUpRequest.user.middleName = middleName;
    }

    if (auth?.continueRegistration?.facebookId) {
      signUpRequest.facebookId = auth?.continueRegistration.facebookId;
    }

    axios
      .post(REGISTER_URL, signUpRequest, {
        withCredentials: true,
      })
      .then((res) => {
        const { id, type, accessToken, role, onboardingStatus } = res?.data;

        setAuth({
          id,
          type,
          username,
          accessToken,
          email,
          role,
          onboardingStatus,
        });

        setLoading(false);
        if (!loading) {
          navigate('/onboarding');
        }
      })
      .catch((err) => {
        const error: ErrorResponse = err.response?.data;

        let message = error.error || 'Failed to register';

        showNotification({
          id: 'failed-to-register',
          message,
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
        setLoading(false);
      });
  };

  const backToggle = () => {
    firstForm.setValues(form.values);
    toggle();
  };
  return (
    <div>
      <Text size='lg' weight={600} mb={20}>
        Last step to finish registration
      </Text>

      <form onSubmit={form.onSubmit(handleRegisterSubmit)} noValidate>
        <Group direction='column' grow>
          <TextInput
            required
            label='First Name'
            placeholder='First Name'
            value={form.values.firstName}
            onChange={(event) =>
              form.setFieldValue('firstName', event.currentTarget.value)
            }
            error={form.errors.firstName}
            maxLength={100}
            data-cy='firstName'
          />

          <TextInput
            label='Middle Name'
            placeholder='Middle Name'
            value={form.values.middleName}
            onChange={(event) =>
              form.setFieldValue('middleName', event.currentTarget.value)
            }
            error={form.errors.middleName}
            maxLength={100}
            disabled={isMNameDisabled}
            data-cy='middleName'
          />

          <Checkbox
            label="I don't have a middle name"
            checked={isMNameDisabled}
            onChange={() => {
              setIsMNameDisabled(!isMNameDisabled);
              form.setFieldValue('middleName', isMNameDisabled ? '' : '.');
            }}
            data-cy='noMiddleName'
          />

          <TextInput
            required
            label='Last Name'
            placeholder='Last Name'
            value={form.values.lastName}
            onChange={(event) =>
              form.setFieldValue('lastName', event.currentTarget.value)
            }
            error={form.errors.lastName}
            maxLength={100}
            data-cy='lastName'
          />

          <TextInput
            type='text'
            className={!masked ? 'pin-disc' : ''}
            required
            label='PIN'
            placeholder='PIN'
            value={form.values.pin}
            onChange={(event) =>
              form.setFieldValue('pin', event.currentTarget.value)
            }
            rightSection={
              <ActionIcon onClick={() => setMasked(!masked)}>
                {masked ? (
                  <svg
                    width='15'
                    height='15'
                    viewBox='0 0 15 15'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M13.3536 2.35355C13.5488 2.15829 13.5488 1.84171 13.3536 1.64645C13.1583 1.45118 12.8417 1.45118 12.6464 1.64645L10.6828 3.61012C9.70652 3.21671 8.63759 3 7.5 3C4.30786 3 1.65639 4.70638 0.0760002 7.23501C-0.0253338 7.39715 -0.0253334 7.60288 0.0760014 7.76501C0.902945 9.08812 2.02314 10.1861 3.36061 10.9323L1.64645 12.6464C1.45118 12.8417 1.45118 13.1583 1.64645 13.3536C1.84171 13.5488 2.15829 13.5488 2.35355 13.3536L4.31723 11.3899C5.29348 11.7833 6.36241 12 7.5 12C10.6921 12 13.3436 10.2936 14.924 7.76501C15.0253 7.60288 15.0253 7.39715 14.924 7.23501C14.0971 5.9119 12.9769 4.81391 11.6394 4.06771L13.3536 2.35355ZM9.90428 4.38861C9.15332 4.1361 8.34759 4 7.5 4C4.80285 4 2.52952 5.37816 1.09622 7.50001C1.87284 8.6497 2.89609 9.58106 4.09974 10.1931L9.90428 4.38861ZM5.09572 10.6114L10.9003 4.80685C12.1039 5.41894 13.1272 6.35031 13.9038 7.50001C12.4705 9.62183 10.1971 11 7.5 11C6.65241 11 5.84668 10.8639 5.09572 10.6114Z'
                      fill='currentColor'
                      fill-rule='evenodd'
                      clip-rule='evenodd'
                    ></path>
                  </svg>
                ) : (
                  <svg
                    width='15'
                    height='15'
                    viewBox='0 0 15 15'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M7.5 11C4.80285 11 2.52952 9.62184 1.09622 7.50001C2.52952 5.37816 4.80285 4 7.5 4C10.1971 4 12.4705 5.37816 13.9038 7.50001C12.4705 9.62183 10.1971 11 7.5 11ZM7.5 3C4.30786 3 1.65639 4.70638 0.0760002 7.23501C-0.0253338 7.39715 -0.0253334 7.60288 0.0760014 7.76501C1.65639 10.2936 4.30786 12 7.5 12C10.6921 12 13.3436 10.2936 14.924 7.76501C15.0253 7.60288 15.0253 7.39715 14.924 7.23501C13.3436 4.70638 10.6921 3 7.5 3ZM7.5 9.5C8.60457 9.5 9.5 8.60457 9.5 7.5C9.5 6.39543 8.60457 5.5 7.5 5.5C6.39543 5.5 5.5 6.39543 5.5 7.5C5.5 8.60457 6.39543 9.5 7.5 9.5Z'
                      fill='currentColor'
                      fill-rule='evenodd'
                      clip-rule='evenodd'
                    ></path>
                  </svg>
                )}
              </ActionIcon>
            }
            error={form.errors.pin}
            maxLength={4}
            data-cy='pin'
          />
        </Group>

        <div
          style={{
            display: 'none',
          }}
        >
          <TextInput
            required
            autoComplete='username'
            value={form.values.username}
          />
          <PasswordInput
            required
            autoComplete='password'
            value={form.values.password}
          />
          <PasswordInput required value={form.values.confirmPassword} />
        </div>

        <Group position='apart' mt='xl'>
          <Button
            type='button'
            variant='outline'
            color='gray'
            onClick={() => backToggle()}
            sx={{ border: 'none' }}
          >
            Back
          </Button>
          <Button type='submit' loading={loading} data-cy='submit'>
            {loading ? 'Loading' : 'Register'}
          </Button>
        </Group>
      </form>
    </div>
  );
}

export default PersonalInfoForm;
